import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Package, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatPriceFixed } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const calcCustomerDiscount = (vendorDisc, margin) => {
  if (margin >= 100) return 0;
  return 100 - ((100 - vendorDisc) / (1 - margin / 100));
};

const VendorProducts = () => {
  const { user } = useOutletContext();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ mrp: 0, vendor_discount_percent: 0, gst_percent: 0 });
  const [searchFilter, setSearchFilter] = useState('');

  const filteredProducts = products.filter(p => {
    if (!searchFilter) return true;
    const keywords = searchFilter.toLowerCase().trim().split(/\s+/);
    const str = `${p.product_name} ${p.product_brand} ${p.product_size} ${p.product_color}`.toLowerCase();
    return keywords.every(kw => str.includes(kw));
  });

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (product) => {
    setEditingId(product.product_id);
    setEditForm({
      mrp: product.mrp || 0,
      vendor_discount_percent: product.vendor_discount_percent || 0,
      gst_percent: product.gst_percent || 0
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ mrp: 0, vendor_discount_percent: 0, gst_percent: 0 });
  };

  const saveEdit = async (productId) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/products/${productId}/vendor-pricing`,
        editForm,
        { withCredentials: true }
      );
      toast.success('Pricing updated');
      setEditingId(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update pricing');
    }
  };

  // Live preview of customer discount while editing
  const liveCustomerDiscount = editForm.mrp > 0
    ? calcCustomerDiscount(editForm.vendor_discount_percent, 0) // vendor doesn't know margin
    : 0;

  if (loading) return <div className="text-stone-600">Loading your products...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" data-testid="vendor-products-title" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          My Products
        </h1>
        <p className="mt-2 text-base text-stone-600">View and update pricing for your products</p>
      </div>

      {/* Search */}
      {products.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-md p-3">
          <Input
            placeholder="Search products (name, brand, size, color)..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="text-xs h-8 border-stone-200"
            data-testid="vendor-product-search"
          />
        </div>
      )}

      {products.length === 0 ? (
        <div className="bg-white rounded-lg border border-stone-200 p-12 text-center">
          <Package className="w-16 h-16 mx-auto text-stone-300 mb-4" />
          <h3 className="text-lg font-medium text-stone-900 mb-2">No Products Yet</h3>
          <p className="text-stone-600">Your products will appear here once added to the catalog.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Product</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">MRP</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">V.Disc%</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">GST%</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Input Price</th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Cust.Disc%</th>
                  <th className="text-center px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {filteredProducts.map((product) => {
                  const isEditing = editingId === product.product_id;
                  const mrp = product.mrp || 0;
                  const vd = product.vendor_discount_percent || 0;
                  const gst = product.gst_percent || 0;
                  const cd = product.customer_discount_percent || 0;

                  const noPricing = mrp <= 0;

                  return (
                    <tr key={product.product_id} data-testid={`vendor-product-row-${product.product_id}`} className={`hover:bg-stone-50 transition-colors ${noPricing ? 'bg-amber-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-stone-900">
                          {product.product_name}
                          {noPricing && <span className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-200 text-amber-900">Price not set</span>}
                        </div>
                        <div className="text-xs text-stone-500">{product.product_brand} • {product.product_size} • {product.product_color}</div>
                      </td>

                      {isEditing ? (
                        <>
                          <td className="px-4 py-3">
                            <Input data-testid={`vendor-mrp-${product.product_id}`} type="number" step="0.01" min="0" value={editForm.mrp} onChange={(e) => setEditForm(f => ({...f, mrp: parseFloat(e.target.value) || 0}))} className="w-24 h-7 text-xs border-stone-300 text-right" />
                          </td>
                          <td className="px-4 py-3">
                            <Input data-testid={`vendor-vdisc-${product.product_id}`} type="number" step="0.01" min="0" max="100" value={editForm.vendor_discount_percent} onChange={(e) => setEditForm(f => ({...f, vendor_discount_percent: parseFloat(e.target.value) || 0}))} className="w-20 h-7 text-xs border-stone-300 text-right" />
                          </td>
                          <td className="px-4 py-3">
                            <Input data-testid={`vendor-gst-${product.product_id}`} type="number" step="0.01" min="0" value={editForm.gst_percent} onChange={(e) => setEditForm(f => ({...f, gst_percent: parseFloat(e.target.value) || 0}))} className="w-20 h-7 text-xs border-stone-300 text-right" />
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-stone-700">
                            {editForm.mrp > 0 ? formatPriceFixed(editForm.mrp * (1 - editForm.vendor_discount_percent / 100) * (1 + editForm.gst_percent / 100)) : '-'}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-stone-500 text-xs italic">Recalculated on save</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex gap-1 justify-center">
                              <Button data-testid={`vendor-save-${product.product_id}`} onClick={() => saveEdit(product.product_id)} size="sm" className="bg-lime-700 hover:bg-lime-800 text-white h-7 w-7 p-0"><Check className="w-3 h-3" /></Button>
                              <Button data-testid={`vendor-cancel-${product.product_id}`} onClick={cancelEdit} size="sm" variant="outline" className="border-stone-300 h-7 w-7 p-0"><X className="w-3 h-3" /></Button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3 text-right tabular-nums text-stone-900">{mrp > 0 ? formatPriceFixed(mrp) : '-'}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-stone-700">{vd > 0 ? `${vd}%` : '-'}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-stone-700">{gst > 0 ? `${gst}%` : '-'}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-stone-700">{(product.input_price || 0) > 0 ? formatPriceFixed(product.input_price) : '-'}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-stone-700">{cd !== 0 ? `${cd.toFixed(2)}%` : '-'}</td>
                          <td className="px-4 py-3 text-center">
                            <Button data-testid={`vendor-edit-pricing-${product.product_id}`} onClick={() => startEdit(product)} size="sm" variant="outline" className="text-xs h-7 border-stone-300">
                              Update Pricing
                            </Button>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
        <h3 className="text-xs font-semibold text-amber-900 mb-1">Pricing Notes:</h3>
        <ul className="text-xs text-amber-800 space-y-0.5 list-disc list-inside">
          <li>You can update MRP, Vendor Discount %, and GST % for your products</li>
          <li>Customer Discount % and selling prices are auto-calculated by the system</li>
          <li>Changes take effect immediately for new orders</li>
        </ul>
      </div>
    </div>
  );
};

export default VendorProducts;
